import { useEffect, useMemo, useState } from 'react';
import { Coins, RefreshCw, Calculator, History, Check, XCircle, Calendar, ChevronDown, ChevronRight, Info } from 'lucide-react';
import type {
  PozoPropinasVista,
  PagoPendientePropinaVista,
  UsuarioVista,
  RepartoVista,
  RepartoSimulacionVista,
  CriterioReparto,
} from '@shared/types';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourPropinas } from '@renderer/tours';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import {
  PresetRango,
  rangoHoy,
  rangoSemana,
  rangoUltimosNDias,
  rangoPersonalizado,
  isoToYYYYMMDD,
} from '@renderer/utils/rangosPropinas';

type Pestana = 'pendientes' | 'reparto' | 'historial';

/**
 * Sprint 12.1b — Página de gestión de propinas (solo dueño).
 *
 * Tres pestañas:
 *  - Pendientes: pozo actual + lista de pagos pendientes con reasignación por-pago.
 *  - Reparto: wizard simulación → confirmar (criterio mesero o igualitario).
 *  - Historial: repartos previos, marcar entregada, cancelar mismo día.
 *
 * Sprint 24 — Prop `embebido`: cuando se monta desde el wizard "Cerrar Día",
 * oculta el header para no duplicar título/icono. Cero refactor: misma página
 * sirve standalone y embebida.
 */
export function PropinasPage({
  embebido = false,
  onCambio,
}: { embebido?: boolean; onCambio?: () => void } = {}) {
  const sesion = useSessionStore((s) => s.sesion);
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  /**
   * Sprint 116 — Embebida en el cierre, se abre donde hay algo que hacer.
   *
   * Dentro del wizard este paso existe **para repartir**: se llega a él porque el cierre dice
   * "$334.00 pendientes de repartir". Arrancar en "Pendientes" —la lista de lo que ya se sabe que
   * está pendiente— obligaba a un salto más para llegar a la única acción de la pantalla.
   *
   * El reparto entre N personas está construido desde hace sprints y la dueña no lo usa. No es que
   * falte la función: es que vive a ocho pasos de donde se necesita (cerrar día → expandir paso 2 →
   * "Hacer reparto" → periodo → criterio → personas → calcular → confirmar). Esto quita uno; el
   * resto del camino se acorta en su propio sprint.
   *
   * Suelta, la pantalla sigue abriendo en "Pendientes", que ahí sí es lo primero que se consulta.
   */
  const [pestana, setPestana] = useState<Pestana>(embebido ? 'reparto' : 'pendientes');
  const [pozo, setPozo] = useState<PozoPropinasVista | null>(null);
  const [pendientes, setPendientes] = useState<PagoPendientePropinaVista[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioVista[]>([]);
  const [historial, setHistorial] = useState<RepartoVista[]>([]);
  const [cargando, setCargando] = useState(false);

  const cargarPozo = async () => {
    const [pz, pe, us] = await Promise.all([
      window.api.propinas.pozo(),
      window.api.propinas.listarPendientes(),
      window.api.usuarios.listarTodos(),
    ]);
    if (pz.ok) setPozo(pz.data);
    if (pe.ok) setPendientes(pe.data);
    if (us.ok) setUsuarios(us.data.filter((u) => u.activo));
  };

  const cargarHistorial = async () => {
    const r = await window.api.propinas.historial(30);
    if (r.ok) setHistorial(r.data);
  };

  useEffect(() => {
    setCargando(true);
    Promise.all([cargarPozo(), cargarHistorial()]).finally(() => setCargando(false));
  }, []);

  const recargar = async () => {
    setCargando(true);
    await Promise.all([cargarPozo(), cargarHistorial()]);
    setCargando(false);
  };

  return (
    <div className="space-y-4">
      {!embebido && (
        <header className="flex items-center justify-between" data-tour="propinas-header">
          <div className="flex items-center gap-3">
            <Coins className="h-7 w-7 text-yellow-600" />
            <div>
              <h1 className="text-2xl font-bold">Propinas</h1>
              <p className="text-xs text-slate-500">Gestión de pozo, reparto y entrega.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={recargar} className="btn-ghost text-sm flex items-center gap-1" disabled={cargando}>
              <RefreshCw className="h-4 w-4" /> Refrescar
            </button>
            <HelpButton tour={tourPropinas} />
          </div>
        </header>
      )}

      {/* Tarjeta pozo siempre visible */}
      {pozo && (
        <div className="rounded-xl bg-gradient-to-br from-yellow-50 to-amber-100 border border-amber-300 px-5 py-4" data-tour="propinas-pozo">
          <div className="text-xs uppercase tracking-wide text-amber-800 font-medium">
            Pozo de propinas pendientes
          </div>
          <div className="text-3xl font-bold text-amber-900 font-mono mt-1">
            {formatPrecio(pozo.totalPendiente, monedaSimbolo, locale)}
          </div>
          <div className="text-xs text-amber-800 mt-1">
            {pozo.cantidadPagos} {pozo.cantidadPagos === 1 ? 'pago' : 'pagos'} sin repartir
          </div>
        </div>
      )}

      {/* Pestañas */}
      <div className="border-b border-slate-200 flex gap-1" data-tour="propinas-tabs">
        {([
          { id: 'pendientes', label: 'Pendientes', icon: Coins },
          { id: 'reparto', label: 'Hacer reparto', icon: Calculator },
          { id: 'historial', label: 'Histórico', icon: History },
        ] as const).map((p) => (
          <button
            key={p.id}
            onClick={() => setPestana(p.id)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 text-sm font-medium ${
              pestana === p.id
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <p.icon className="h-4 w-4" />
            {p.label}
          </button>
        ))}
      </div>

      {pestana === 'pendientes' && (
        <PestanaPendientes
          pozo={pozo}
          pendientes={pendientes}
          usuarios={usuarios}
          monedaSimbolo={monedaSimbolo}
          locale={locale}
          onRecargar={cargarPozo}
        />
      )}

      {pestana === 'reparto' && sesion && (
        <PestanaReparto
          usuarios={usuarios}
          monedaSimbolo={monedaSimbolo}
          locale={locale}
          usuarioEjecutorId={sesion.usuarioId}
          onConfirmado={async () => {
            await recargar();
            setPestana('historial');
            // Sprint 43 — notificar al padre embebido para que recargue su estado.
            onCambio?.();
          }}
        />
      )}

      {pestana === 'historial' && sesion && (
        <PestanaHistorial
          historial={historial}
          monedaSimbolo={monedaSimbolo}
          locale={locale}
          usuarioId={sesion.usuarioId}
          onRecargar={async () => {
            await recargar();
            // Sprint 43 — cancelar reparto cambia el pozo: notificar.
            onCambio?.();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// PESTAÑA: PENDIENTES (lista con reasignación)
// ============================================================================

function PestanaPendientes(props: {
  pozo: PozoPropinasVista | null;
  pendientes: PagoPendientePropinaVista[];
  usuarios: UsuarioVista[];
  monedaSimbolo: string;
  locale: string;
  onRecargar: () => Promise<void>;
}) {
  const { pozo, pendientes, usuarios, monedaSimbolo, locale, onRecargar } = props;

  const reasignar = async (pagoId: number, nuevoUsuarioId: number) => {
    const r = await window.api.propinas.reasignarPago({ pagoId, nuevoUsuarioId });
    if (!r.ok) {
      window.alertar({ titulo: 'Error', mensaje: r.error, variant: 'warn' });
    } else {
      await onRecargar();
    }
  };

  return (
    <div className="space-y-4">
      {pozo && pozo.desglosePorMesero.length > 0 && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-2">Desglose por mesero atribuido</h3>
          <ul className="space-y-1 text-sm">
            {pozo.desglosePorMesero.map((d) => (
              <li key={d.usuarioId ?? 'sin'} className="flex justify-between">
                <span>{d.usuarioNombre} <span className="text-slate-400">({d.cantidad} pagos)</span></span>
                <span className="font-mono font-semibold">{formatPrecio(d.total, monedaSimbolo, locale)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <h3 className="font-semibold text-sm mb-2">
          Pagos pendientes ({pendientes.length})
        </h3>
        {pendientes.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            No hay propinas pendientes. Cuando los clientes paguen con propina, aparecerán aquí.
          </p>
        ) : (
          <div className="max-h-80 overflow-x-auto overflow-y-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b">
                  <th className="py-2 pr-2">Pedido</th>
                  <th className="py-2 pr-2">Fecha</th>
                  <th className="py-2 pr-2 text-right">Propina</th>
                  <th className="py-2 pr-2">Atribuida a</th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map((p) => (
                  <tr key={p.pagoId} className="border-b border-slate-100">
                    <td className="py-2 pr-2 font-mono">#{String(p.pedidoNumero).padStart(5, '0')}</td>
                    <td className="py-2 pr-2 text-xs text-slate-500">
                      {new Date(p.fechaCreacion).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="py-2 pr-2 text-right font-mono font-semibold">
                      {formatPrecio(p.propina, monedaSimbolo, locale)}
                    </td>
                    <td className="py-2 pr-2">
                      <select
                        className="input text-sm py-1"
                        value={p.propinaUsuarioId ?? ''}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (v > 0) reasignar(p.pagoId, v);
                        }}
                      >
                        <option value="">— Sin asignar —</option>
                        {usuarios.map((u) => (
                          <option key={u.id} value={u.id}>{u.nombre}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PESTAÑA: REPARTO (wizard)
// ============================================================================

function PestanaReparto(props: {
  usuarios: UsuarioVista[];
  monedaSimbolo: string;
  locale: string;
  usuarioEjecutorId: number;
  onConfirmado: () => Promise<void>;
}) {
  const { usuarios, monedaSimbolo, locale, usuarioEjecutorId, onConfirmado } = props;

  // Sprint 41 — Presets en vez de calendarios. El rango se computa cada vez
  // que cambia el preset o `nDias`, en hora LOCAL del usuario.
  const [preset, setPreset] = useState<PresetRango>('hoy');
  const [nDias, setNDias] = useState<number>(7);
  // Personalizado (oculto detrás del disclosure)
  const hoyYYYYMMDD = useMemo(() => isoToYYYYMMDD(new Date().toISOString()), []);
  const [fechaInicio, setFechaInicio] = useState<string>(hoyYYYYMMDD);
  const [fechaFin, setFechaFin] = useState<string>(hoyYYYYMMDD);
  const [mostrarPersonalizado, setMostrarPersonalizado] = useState<boolean>(false);

  // Resumen en vivo de cada preset (monto + cantidad pagos pendientes).
  // Se recarga al montar y tras un reparto exitoso (via key del padre).
  const [resumenHoy, setResumenHoy] = useState<{ totalPendiente: number; cantidadPagos: number } | null>(null);
  const [resumenSemana, setResumenSemana] = useState<{ totalPendiente: number; cantidadPagos: number } | null>(null);
  const [resumenN, setResumenN] = useState<{ totalPendiente: number; cantidadPagos: number } | null>(null);
  const [rangoPendientes, setRangoPendientes] = useState<{ fechaInicio: string; fechaFin: string } | null>(null);

  const [criterio, setCriterio] = useState<CriterioReparto>('mesero');
  const [seleccionados, setSeleccionados] = useState<number[]>([]);
  const [simulacion, setSimulacion] = useState<RepartoSimulacionVista | null>(null);
  const [simulando, setSimulando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar resúmenes de los 3 presets en paralelo + rango natural del pozo
  // para el banner orientativo si todo está vacío.
  const recargarResumenes = async () => {
    const rHoy = rangoHoy();
    const rSem = rangoSemana();
    const rN = rangoUltimosNDias(nDias) ?? rangoHoy();
    const [h, s, n, rp] = await Promise.all([
      window.api.propinas.resumenPorRango(rHoy.fechaInicio, rHoy.fechaFin),
      window.api.propinas.resumenPorRango(rSem.fechaInicio, rSem.fechaFin),
      window.api.propinas.resumenPorRango(rN.fechaInicio, rN.fechaFin),
      window.api.propinas.rangoPendientes(),
    ]);
    if (h.ok) setResumenHoy(h.data);
    if (s.ok) setResumenSemana(s.data);
    if (n.ok) setResumenN(n.data);
    if (rp.ok) setRangoPendientes(rp.data);
  };

  useEffect(() => {
    recargarResumenes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El "últimos N días" recalcula al cambiar N (debounced inline por simplicidad).
  useEffect(() => {
    const r = rangoUltimosNDias(nDias);
    if (!r) return;
    let alive = true;
    (async () => {
      const res = await window.api.propinas.resumenPorRango(r.fechaInicio, r.fechaFin);
      if (alive && res.ok) setResumenN(res.data);
    })();
    return () => { alive = false; };
  }, [nDias]);

  // Computa el rango ISO actual según el preset seleccionado.
  // Esto es el que se manda al backend al simular/ejecutar.
  const rangoActual = useMemo(() => {
    if (preset === 'hoy') return rangoHoy();
    if (preset === 'semana') return rangoSemana();
    if (preset === 'ultimos_n_dias') return rangoUltimosNDias(nDias) ?? rangoHoy();
    return rangoPersonalizado(fechaInicio, fechaFin);
  }, [preset, nDias, fechaInicio, fechaFin]);

  // Pozo total visible y si los 3 presets estándar dan 0 → banner orientativo.
  const presetsTodosVacios =
    (resumenHoy?.cantidadPagos ?? 0) === 0 &&
    (resumenSemana?.cantidadPagos ?? 0) === 0 &&
    (resumenN?.cantidadPagos ?? 0) === 0;
  const hayPozoPeroVacio = presetsTodosVacios && rangoPendientes !== null;

  const cargarDesdePrimerPagoPendiente = () => {
    if (!rangoPendientes) return;
    setPreset('personalizado');
    setMostrarPersonalizado(true);
    setFechaInicio(isoToYYYYMMDD(rangoPendientes.fechaInicio));
    setFechaFin(isoToYYYYMMDD(rangoPendientes.fechaFin));
    setSimulacion(null);
  };

  const toggleSeleccion = (id: number) => {
    setSeleccionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setSimulacion(null);
  };

  const simular = async () => {
    setError(null);
    setSimulando(true);
    setSimulacion(null);
    const r = await window.api.propinas.simularReparto({
      fechaInicio: rangoActual.fechaInicio,
      fechaFin: rangoActual.fechaFin,
      criterio,
      usuariosIgualitario: criterio === 'igualitario' ? seleccionados : undefined,
    });
    setSimulando(false);
    if (r.ok) setSimulacion(r.data);
    else setError(traducirErrorBackend(r.error));
  };

  const confirmar = async () => {
    if (!simulacion) return;
    const ok = await window.confirmar({
      titulo: 'Confirmar reparto',
      mensaje: `Se repartirán ${formatPrecio(simulacion.totalAReparte, monedaSimbolo, locale)} entre ${simulacion.detalles.length} empleado(s). Esta acción marca los pagos como repartidos.`,
      textoConfirmar: 'Sí, confirmar reparto',
      variant: 'warn',
    });
    if (!ok) return;
    setConfirmando(true);
    setError(null);
    const r = await window.api.propinas.ejecutarReparto({
      fechaInicio: rangoActual.fechaInicio,
      fechaFin: rangoActual.fechaFin,
      criterio,
      usuariosIgualitario: criterio === 'igualitario' ? seleccionados : undefined,
      usuarioEjecutorId,
    });
    setConfirmando(false);
    if (r.ok) {
      await onConfirmado();
      await recargarResumenes();
    } else {
      setError(traducirErrorBackend(r.error));
    }
  };

  const seleccionarPreset = (p: PresetRango) => {
    setPreset(p);
    setSimulacion(null);
  };

  return (
    <div className="space-y-4">
      <div className="card space-y-4">
        <h3 className="font-semibold text-sm">Paso 1 — ¿De qué periodo quieres repartir?</h3>

        {/* Sprint 41 — Tarjetas de presets con monto/conteo en vivo */}
        <div className="grid gap-3 sm:grid-cols-3">
          <TarjetaPreset
            activa={preset === 'hoy'}
            onClick={() => seleccionarPreset('hoy')}
            titulo="Hoy"
            subtitulo="De medianoche a ahora"
            resumen={resumenHoy}
            monedaSimbolo={monedaSimbolo}
            locale={locale}
          />
          <TarjetaPreset
            activa={preset === 'semana'}
            onClick={() => seleccionarPreset('semana')}
            titulo="Esta semana"
            subtitulo="Desde el lunes"
            resumen={resumenSemana}
            monedaSimbolo={monedaSimbolo}
            locale={locale}
          />
          <TarjetaPreset
            activa={preset === 'ultimos_n_dias'}
            onClick={() => seleccionarPreset('ultimos_n_dias')}
            titulo={`Últimos ${nDias} días`}
            subtitulo="Incluyendo hoy"
            resumen={resumenN}
            monedaSimbolo={monedaSimbolo}
            locale={locale}
            extra={
              <div className="flex items-center gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
                <label className="text-xs text-slate-600">Días:</label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={nDias}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(90, Number(e.target.value) || 1));
                    setNDias(v);
                    if (preset === 'ultimos_n_dias') setSimulacion(null);
                  }}
                  className="w-16 rounded border border-slate-300 px-2 py-1 text-sm"
                />
              </div>
            }
          />
        </div>

        {/* Banner orientativo si los 3 presets dan 0 pero hay pozo */}
        {hayPozoPeroVacio && rangoPendientes && (
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
            <Info className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm text-amber-900">
              <div className="font-semibold">No hay propinas en los periodos rápidos, pero el pozo tiene saldo.</div>
              <div className="text-xs mt-0.5">
                El primer pago pendiente es del{' '}
                <strong>
                  {new Date(rangoPendientes.fechaInicio).toLocaleDateString('es-MX', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'short',
                  })}
                </strong>
                . Puedes cargar el rango completo de pagos pendientes:
              </div>
              <button
                onClick={cargarDesdePrimerPagoPendiente}
                className="mt-2 inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Cargar desde el primer pago pendiente →
              </button>
            </div>
          </div>
        )}

        {/* Disclosure de rango personalizado */}
        <div>
          <button
            type="button"
            onClick={() => setMostrarPersonalizado((v) => !v)}
            className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
          >
            {mostrarPersonalizado ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <Calendar className="h-3.5 w-3.5" />
            Rango personalizado (avanzado)
          </button>

          {mostrarPersonalizado && (
            <div className="mt-3 rounded-lg border border-slate-200 p-3 space-y-3 bg-slate-50">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="radio"
                  id="preset-custom"
                  checked={preset === 'personalizado'}
                  onChange={() => seleccionarPreset('personalizado')}
                />
                <label htmlFor="preset-custom" className="cursor-pointer">
                  Usar rango personalizado
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label text-xs">Desde</label>
                  <input
                    type="date"
                    className="input"
                    value={fechaInicio}
                    onChange={(e) => {
                      setFechaInicio(e.target.value);
                      if (preset === 'personalizado') setSimulacion(null);
                    }}
                  />
                </div>
                <div>
                  <label className="label text-xs">Hasta</label>
                  <input
                    type="date"
                    className="input"
                    value={fechaFin}
                    onChange={(e) => {
                      setFechaFin(e.target.value);
                      if (preset === 'personalizado') setSimulacion(null);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <h3 className="font-semibold text-sm mt-2">Paso 2 — Criterio</h3>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={criterio === 'mesero'} onChange={() => { setCriterio('mesero'); setSimulacion(null); }} />
            <span>Por mesero atribuido</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={criterio === 'igualitario'} onChange={() => { setCriterio('igualitario'); setSimulacion(null); }} />
            <span>Igualitario entre N personas</span>
          </label>
        </div>

        {criterio === 'igualitario' && (
          <div>
            <div className="label text-xs">Seleccionar empleados a recibir:</div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {usuarios.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border border-slate-200 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={seleccionados.includes(u.id)}
                    onChange={() => toggleSeleccion(u.id)}
                  />
                  <span>
                    {u.nombre}
                    <span className="block text-xs text-slate-500">
                      {u.rol === 'dueño' ? 'Dueño/a' : u.activoLogin === false ? 'Sin acceso al POS' : 'Mesero'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {seleccionados.length === 0 && (
              <p className="text-xs text-amber-700 mt-2">Selecciona al menos 1 empleado.</p>
            )}
          </div>
        )}

        <button
          onClick={simular}
          className="btn-primary"
          disabled={simulando || (criterio === 'igualitario' && seleccionados.length === 0)}
        >
          {simulando ? 'Calculando...' : 'Calcular preview'}
        </button>
      </div>

      {simulacion && (
        <div className="card space-y-3">
          <h3 className="font-semibold text-sm">Paso 3 — Vista previa</h3>
          <div className="text-2xl font-bold font-mono text-primary">
            Total a repartir: {formatPrecio(simulacion.totalAReparte, monedaSimbolo, locale)}
          </div>
          <div className="text-xs text-slate-500">
            {simulacion.cantidadPagos} pago(s) en el rango.
          </div>
          {/* Sprint 12.1c — aviso si total=0 pero pozo>0 (rango mal seleccionado) */}
          {simulacion.totalAReparte === 0 && simulacion.cantidadPagos === 0 && (
            <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              No hay propinas en este rango de fechas. Si el pozo tiene saldo, es de
              días fuera del rango — amplía las fechas arriba y vuelve a calcular.
            </div>
          )}
          {/* Sprint 158 (DT-067) — Lo que no tiene mesero no se reparte: se dice, no se esconde. */}
          {(simulacion.montoSinAsignar ?? 0) > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {formatPrecio(simulacion.montoSinAsignar, monedaSimbolo, locale)} de propina no tienen
              mesero asignado: <b>no entran en este reparto</b> y se quedan en el pozo. Asígnalas en
              Pendientes, o repártelas por igual.
            </div>
          )}
          {simulacion.detalles.length === 0 && simulacion.cantidadPagos > 0 ? (
            <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              Ningún empleado recibe propina. Revisa atribución en Pendientes o agrega usuarios al reparto igualitario.
            </div>
          ) : simulacion.detalles.length > 0 ? (
            <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b text-xs uppercase text-slate-500">
                  <th className="py-2 pr-2">Empleado</th>
                  <th className="py-2 pr-2 text-right">Monto</th>
                </tr>
              </thead>
              <tbody>
                {simulacion.detalles.map((d) => (
                  <tr key={d.usuarioId} className="border-b border-slate-100">
                    <td className="py-2 pr-2">{d.usuarioNombre}</td>
                    <td className="py-2 pr-2 text-right font-mono font-semibold">
                      {formatPrecio(d.monto, monedaSimbolo, locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : null}

          {error && <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}

          <button
            onClick={confirmar}
            className="btn-primary btn-lg w-full"
            disabled={confirmando || simulacion.detalles.length === 0}
          >
            <Check className="h-5 w-5" />
            {confirmando ? 'Confirmando...' : 'Confirmar y ejecutar reparto'}
          </button>
        </div>
      )}

      {error && !simulacion && (
        <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
    </div>
  );
}

/**
 * Sprint 41 — Tarjeta de preset de rango: titulo + subtitulo + monto/conteo
 * en vivo. Resalta cuando está activa; al hover sube ligeramente.
 */
function TarjetaPreset(props: {
  activa: boolean;
  onClick: () => void;
  titulo: string;
  subtitulo: string;
  resumen: { totalPendiente: number; cantidadPagos: number } | null;
  monedaSimbolo: string;
  locale: string;
  extra?: React.ReactNode;
}) {
  const { activa, onClick, titulo, subtitulo, resumen, monedaSimbolo, locale, extra } = props;
  const tieneMonto = resumen !== null && resumen.cantidadPagos > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-start gap-1 rounded-xl border-2 p-4 text-left transition ${
        activa
          ? 'border-primary bg-primary/5 shadow'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
      }`}
      aria-pressed={activa}
    >
      <div className="text-base font-bold text-slate-900">{titulo}</div>
      <div className="text-xs text-slate-500">{subtitulo}</div>
      <div className={`mt-1 font-mono text-2xl font-bold ${tieneMonto ? 'text-emerald-700' : 'text-slate-400'}`}>
        {resumen ? formatPrecio(resumen.totalPendiente, monedaSimbolo, locale) : '…'}
      </div>
      <div className="text-xs text-slate-500">
        {resumen
          ? `${resumen.cantidadPagos} ${resumen.cantidadPagos === 1 ? 'pago' : 'pagos'} pendiente${resumen.cantidadPagos === 1 ? '' : 's'}`
          : ''}
      </div>
      {extra}
    </button>
  );
}

// ============================================================================
// PESTAÑA: HISTORIAL (con marcar entregada + cancelar)
// ============================================================================

function PestanaHistorial(props: {
  historial: RepartoVista[];
  monedaSimbolo: string;
  locale: string;
  usuarioId: number;
  onRecargar: () => Promise<void>;
}) {
  const { historial, monedaSimbolo, locale, usuarioId, onRecargar } = props;
  const [expandido, setExpandido] = useState<number | null>(null);

  const marcarEntregada = async (detalleId: number, entregada: boolean) => {
    const r = await window.api.propinas.marcarEntregada({ detalleId, entregada });
    if (!r.ok) {
      window.alertar({ titulo: 'Error', mensaje: r.error, variant: 'warn' });
    } else {
      await onRecargar();
    }
  };

  const cancelar = async (repartoId: number) => {
    const ok = await window.confirmar({
      titulo: 'Cancelar reparto',
      mensaje: 'El reparto se revertirá y se registrará un INGRESO en caja por DEVOLUCION_PROPINA. Solo permitido el mismo día.',
      textoConfirmar: 'Sí, cancelar',
      variant: 'destructive',
    });
    if (!ok) return;
    const motivo = window.prompt('Motivo de cancelación (mínimo 5 caracteres):');
    if (!motivo || motivo.trim().length < 5) {
      window.alertar({ titulo: 'Motivo inválido', mensaje: 'Debe tener al menos 5 caracteres', variant: 'warn' });
      return;
    }
    const r = await window.api.propinas.cancelarReparto({ repartoId, motivo: motivo.trim(), usuarioId });
    if (!r.ok) {
      window.alertar({ titulo: 'Error', mensaje: r.error, variant: 'warn' });
    } else {
      await onRecargar();
    }
  };

  if (historial.length === 0) {
    return (
      <div className="card text-center py-8 text-slate-500 text-sm">
        Aún no hay repartos. Cuando hagas tu primer reparto aparecerá aquí.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {historial.map((r) => {
        const esCancelado = r.estado === 'cancelado';
        const esHoy = new Date(r.fechaEjecucion).toDateString() === new Date().toDateString();
        return (
          <div key={r.id} className={`card ${esCancelado ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Reparto #{r.id}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    esCancelado ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {esCancelado ? 'Cancelado' : 'Confirmado'}
                  </span>
                  <span className="text-xs text-slate-500">{r.criterio === 'mesero' ? 'por mesero' : 'igualitario'}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {new Date(r.fechaEjecucion).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                  {r.usuarioEjecutorNombre && ` · ${r.usuarioEjecutorNombre}`}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-lg">
                  {formatPrecio(r.totalRepartido, monedaSimbolo, locale)}
                </div>
                <button
                  onClick={() => setExpandido(expandido === r.id ? null : r.id)}
                  className="btn-ghost text-xs"
                >
                  {expandido === r.id ? 'Ocultar' : 'Ver detalles'}
                </button>
              </div>
            </div>

            {expandido === r.id && (
              <div className="mt-3 border-t pt-3 space-y-2">
                {esCancelado && r.motivoCancelacion && (
                  <div className="text-xs text-red-700 bg-red-50 p-2 rounded border border-red-200">
                    <strong>Motivo:</strong> {r.motivoCancelacion}
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b text-xs uppercase text-slate-500">
                      <th className="py-1 pr-2">Empleado</th>
                      <th className="py-1 pr-2 text-right">Monto</th>
                      <th className="py-1 pr-2 text-center">Entregada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.detalles.map((d) => (
                      <tr key={d.detalleId} className="border-b border-slate-100">
                        <td className="py-1 pr-2">{d.usuarioNombre}</td>
                        <td className="py-1 pr-2 text-right font-mono">{formatPrecio(d.monto, monedaSimbolo, locale)}</td>
                        <td className="py-1 pr-2 text-center">
                          <label className="inline-flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={d.entregada}
                              disabled={esCancelado}
                              onChange={(e) => marcarEntregada(d.detalleId, e.target.checked)}
                            />
                            {d.entregada && d.fechaEntrega && (
                              <span className="text-xs text-green-700">
                                {new Date(d.fechaEntrega).toLocaleDateString('es-MX')}
                              </span>
                            )}
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!esCancelado && esHoy && (
                  <button
                    onClick={() => cancelar(r.id)}
                    className="btn-ghost text-xs text-red-600 flex items-center gap-1"
                  >
                    <XCircle className="h-3 w-3" /> Cancelar reparto (solo hoy)
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
